import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trophy, Users, Dice5, Loader2, ListChecks, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import DrawReveal from "@/components/DrawReveal";
import { Button } from "@/components/ui/button";

const GOLD = {
  background: "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
  color: "#1a1410",
};

/** Quiz draw (admin + owning merchant): pool = players with a perfect score. Definitive. */
export default function QuizDrawPanel({ game }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reveal, setReveal] = useState(null);

  useEffect(() => {
    if (!game?.id) return;
    api.get(`/games/${game.id}/quiz/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [game?.id, game?.winners_count]);

  async function runDraw() {
    if (drawing) return;
    setDrawing(true);
    setReveal({ phase: "countdown", winners: [] });
    const suspense = new Promise((res) => setTimeout(res, 5000));
    try {
      const [{ data }] = await Promise.all([api.post(`/games/${game.id}/quiz/draw`), suspense]);
      setStats((s) => ({ ...(s || {}), drawn_at: data.drawn_at, winners: data.winners || [] }));
      setReveal({ phase: "winners", winners: data.winners || [] });
    } catch (e) {
      setReveal(null);
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
      setConfirming(false);
    }
  }

  const winners = stats?.winners || [];
  const drawnAt = stats?.drawn_at || null;
  const eligible = stats?.eligible_count || 0;

  return (
    <div className="qrise-card p-6" data-testid="quiz-draw-panel">
      <DrawReveal
        open={!!reveal}
        phase={reveal?.phase}
        participants={eligible}
        winners={reveal?.winners || []}
        showTickets
        onClose={() => setReveal(null)}
      />
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="h-4 w-4" style={{ color: "#d4af37" }} />
        <div className="text-sm font-semibold">{t("quiz_draw.title")}</div>
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug mb-4">
        {t("quiz_draw.subtitle", { count: stats?.winners_count ?? game.winners_count ?? 1 })}
      </p>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-xl border border-border/60 p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
            <Users className="h-3 w-3" />
            {t("quiz_draw.participants")}
          </div>
          <div className="mt-0.5 text-2xl font-semibold tracking-tight" data-testid="quiz-draw-participants">
            {stats?.total_participants ?? "—"}
          </div>
        </div>
        <div className="rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1.5">
            <ListChecks className="h-3 w-3" />
            {t("quiz_draw.eligible")}
          </div>
          <div className="mt-0.5 text-2xl font-semibold tracking-tight" data-testid="quiz-draw-eligible">
            {stats?.eligible_count ?? "—"}
          </div>
          {stats?.total_tickets > (stats?.eligible_count || 0) && (
            <div className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80" data-testid="quiz-draw-tickets">
              {t("quiz_draw.tickets_total", { count: stats.total_tickets })}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11px] text-muted-foreground">
          {drawnAt ? "" : eligible ? t("quiz_draw.definitive_note") : t("quiz_draw.no_eligible")}
        </div>
        {drawnAt ? (
          <div className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400" data-testid="quiz-draw-done-note">
            {t("quiz_draw.drawn_at", { date: new Date(drawnAt).toLocaleString() })}
            <div className="font-normal text-muted-foreground">{t("quiz_draw.done_note")}</div>
          </div>
        ) : confirming ? (
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => setConfirming(false)} disabled={drawing}
              className="h-10 rounded-xl text-xs" data-testid="quiz-draw-cancel-btn">
              {t("quiz_draw.cancel")}
            </Button>
            <Button type="button" onClick={runDraw} disabled={drawing}
              className="h-10 rounded-xl text-xs font-bold tracking-[0.14em] uppercase" style={GOLD}
              data-testid="quiz-draw-confirm-btn">
              {drawing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              {drawing ? t("quiz_draw.drawing") : t("quiz_draw.confirm_btn")}
            </Button>
          </div>
        ) : (
          <Button type="button" onClick={() => setConfirming(true)} disabled={!eligible}
            className="h-10 rounded-xl text-xs font-bold tracking-[0.2em] uppercase"
            style={{ ...GOLD, boxShadow: "0 10px 30px -8px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.35)" }}
            data-testid="quiz-draw-run-btn">
            <Dice5 className="h-4 w-4 mr-1.5" />
            {t("quiz_draw.draw_btn")}
          </Button>
        )}
      </div>

      {winners.length > 0 && (
        <div className="mt-4 rounded-xl border border-amber-400/40 bg-amber-400/10 p-4" data-testid="quiz-draw-winners-list">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5 mb-3">
            <Trophy className="h-3.5 w-3.5" />
            {t("quiz_draw.winners_title", { count: winners.length })}
          </div>
          <ul className="space-y-2">
            {winners.map((w, i) => (
              <li key={w.session_id} className="flex items-center gap-3 rounded-lg bg-card border border-border px-3 py-2"
                data-testid={`quiz-draw-winner-${i}`}>
                <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold" style={GOLD}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{w.first_name} {w.last_name}</div>
                  <div className="text-xs text-muted-foreground font-mono truncate">
                    {w.email}{w.phone ? ` · ${w.phone}` : ""}
                  </div>
                </div>
                <div className="text-right shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <div className="font-bold text-amber-700 dark:text-amber-300">
                    {w.tickets || 1} {t("lottery.tickets")}
                  </div>
                  {t("quiz_draw.attempts", { count: w.attempts || 1 })}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {winners.length > 0 && game.win_email_message?.trim() && (
        <div className="mt-4 rounded-xl border border-border/60 bg-muted/40 p-4" data-testid="quiz-draw-winner-message">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">
              {t("quiz_draw.winner_message_title")}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 rounded-lg text-[11px]"
              onClick={() => {
                navigator.clipboard?.writeText(game.win_email_message).then(
                  () => toast.success(t("quiz_draw.winner_message_copied")),
                  () => toast.error(t("play.lottery_copy_error"))
                );
              }}
              data-testid="quiz-draw-copy-winner-message-btn"
            >
              <Copy className="h-3 w-3 mr-1" />
              {t("quiz_draw.winner_message_copy")}
            </Button>
          </div>
          <p className="text-sm whitespace-pre-line">{game.win_email_message}</p>
          <p className="mt-2 text-[11px] text-muted-foreground">{t("quiz_draw.winner_message_hint")}</p>
        </div>
      )}
    </div>
  );
}
